/**
 * E03-F02-T11 — "Company Facts" panel: what «Search Company Website» actually
 * produced.
 *
 * Before T11 the button opened a Google tab and the recruiter got nothing back in
 * the app. Now the api downloads the client's own website and extracts the company
 * profile with Haiku (no `web_search`), persists it to `projects.data.company_facts`
 * and THIS panel is where the human sees it — with the pages we actually read, so
 * the facts are checkable rather than model recall.
 *
 * Thin shell: all mapping lives in `./companyFactsModel.ts`. Renders nothing when
 * the project has no collected facts (parity with `KickoffDataPanel`).
 */
import type { CompanyWebsiteFacts } from '@eer/shared';
import {
  buildCompanyFactRows,
  collectedAtLabel,
  isCompanyFactsEmpty,
} from './companyFactsModel.js';

export interface CompanyFactsPanelProps {
  /** The `projects.data.company_facts` bag (null when never collected). */
  readonly facts?: CompanyWebsiteFacts | null;
  /** Whether the `<details>` starts expanded. Default true. */
  readonly defaultOpen?: boolean;
}

export function CompanyFactsPanel({
  facts,
  defaultOpen = true,
}: CompanyFactsPanelProps): React.JSX.Element | null {
  if (!facts || isCompanyFactsEmpty(facts)) return null;

  const rows = buildCompanyFactRows(facts);
  const date = collectedAtLabel(facts);

  return (
    <details
      data-testid="kickoff-company-facts"
      className="rounded-lg border border-border p-4"
      open={defaultOpen}
    >
      <summary className="cursor-pointer select-none text-sm font-bold text-text-strong">
        <i className="fa-solid fa-globe mr-1 text-blue-500" aria-hidden />
        Company Facts
        {facts.domain ? (
          <span data-testid="kickoff-company-facts-domain" className="ml-1 text-xs font-normal">
            <a
              href={`https://${facts.domain}`}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline"
            >
              {facts.domain}
            </a>
          </span>
        ) : null}
        {date ? (
          <span
            data-testid="kickoff-company-facts-date"
            className="ml-1 text-xs font-normal text-text-muted"
          >
            {date}
          </span>
        ) : null}
      </summary>

      <div className="mt-2 space-y-2 text-xs text-slate-700">
        {facts.summary ? (
          <p data-testid="kickoff-company-facts-summary" className="whitespace-pre-wrap">
            {facts.summary}
          </p>
        ) : null}

        {rows.length > 0 ? (
          <table className="w-full">
            <tbody>
              {rows.map((r) => (
                <tr key={r.label} data-testid="kickoff-company-facts-row">
                  <td className="whitespace-nowrap py-1 pr-3 align-top text-text-muted">
                    {r.label}
                  </td>
                  <td className="py-1">{r.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}

        {facts.facts.length > 0 ? (
          <ul data-testid="kickoff-company-facts-list" className="ml-4 list-disc space-y-0.5">
            {facts.facts.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        ) : null}

        {/* Provenance — the pages we downloaded. The whole point of replacing the
            Google link is that the recruiter can verify where a fact came from. */}
        {facts.page_urls.length > 0 ? (
          <div data-testid="kickoff-company-facts-pages" className="pt-1 text-text-muted">
            Pages read:{' '}
            {facts.page_urls.map((url, i) => (
              <span key={url}>
                {i > 0 ? ', ' : ''}
                <a href={url} target="_blank" rel="noreferrer" className="underline">
                  {url}
                </a>
              </span>
            ))}
          </div>
        ) : null}
      </div>
    </details>
  );
}

export default CompanyFactsPanel;
